import { useEffect, useState, useLayoutEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import ContentRenderer from '../components/content/ContentRenderer/ContentRenderer'
import LessonVideoSlot from '../components/course/LessonVideoSlot'
import { SectionErrorFallback } from '../components/layout/ErrorBoundary/ErrorBoundary'
import { useUser } from '../contexts/user'
import { useSuppressDrawing } from '../contexts/drawing'
import { authApi } from '../lib/auth'
import type { CourseDetail, LessonDetail } from '../lib/content'
import { lessonKindMeta, lessonDisplayTitle } from '../config/lessonKind'
import { EVENT, trackCheckout } from '../utils/track'
import { authUrlForCheckout } from '../lib/checkoutIntent'
import LessonDoneButton from '../components/course/LessonDoneButton'
import {
  FaArrowLeft,
  FaArrowRight,
  FaChevronDown,
  FaChevronRight,
  FaBookOpen,
  FaCheckCircle,
  FaLock,
} from 'react-icons/fa'

interface LessonNav { slug: string; title: string }

interface LessonPageProps {
  course: CourseDetail | null
  lesson: LessonDetail | null
  prev: LessonNav | null
  next: LessonNav | null
  lessonIndex: number
  totalLessons: number
}

function LessonPage({ course, lesson, prev, next, lessonIndex, totalLessons }: LessonPageProps) {
  const { slug, lessonSlug } = useParams<{ slug: string; lessonSlug: string }>()
  const navigate = useNavigate()
  const { user, entitlements } = useUser()

  const modules = course?.modules ?? []
  const currentModule = modules.find((m) => m.slug === lesson?.module.slug) || modules[0] || null
  const entitled = !!course && entitlements.includes(course.productSlug)
  const lockedForUser = !!course && course.paid && !entitled

  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [openModules, setOpenModules] = useState<Set<string>>(() =>
    currentModule ? new Set([currentModule.slug]) : new Set(),
  )
  const [checkoutError, setCheckoutError] = useState('')
  const [checkoutBusy, setCheckoutBusy] = useState(false)

  useEffect(() => {
    if (currentModule) setOpenModules((prev) => new Set([...Array.from(prev), currentModule.slug]))
    window.scrollTo(0, 0)
  }, [currentModule, lessonSlug])

  useLayoutEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header') as HTMLElement | null
      const stickyNavEl = document.querySelector('.sticky.top-0') as HTMLElement | null
      const headerH = headerEl?.offsetHeight ?? 0
      const navH = stickyNavEl?.offsetHeight ?? 0
      document.documentElement.style.setProperty('--header-nav-height', `${headerH + navH}px`)
    }
    measure()
    requestAnimationFrame(() => measure())
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  // A locked lesson renders a paywall instead of its content, so suppress the
  // global drawing layer — ink anchored to lesson content would otherwise land
  // on the paywall or leak the owner's notes on content the user can't see.
  // Also suppress below 1536px: the lesson layout gives up ~320–384px to the
  // sidebar, so the content column is far narrower than the viewport and the
  // annotation layer (which the global DrawingLayer only hides below 1300)
  // would misalign against it here.
  const narrowForDrawing = useBelowWidth(1536)
  useSuppressDrawing(!!lesson?.locked || narrowForDrawing)

  const toggleModule = (moduleSlug: string) => {
    setOpenModules((prev) => {
      const next = new Set(prev)
      if (next.has(moduleSlug)) next.delete(moduleSlug)
      else next.add(moduleSlug)
      return next
    })
  }

  // Which lessons this reader has already marked done, so the button under the
  // body opens in the right state rather than flicking after a round trip.
  const [done, setDone] = useState<string[]>([])
  useEffect(() => {
    if (!user || !slug) { setDone([]); return }
    let cancelled = false
    void authApi.courseProgress(slug).then((p) => { if (!cancelled && p) setDone(p.completed) })
    return () => { cancelled = true }
  }, [user, slug])

  async function handleCheckout() {
    if (!course) return
    // Same three outcomes as the course page, tagged as the paywall placement so
    // a locked lesson can be compared with the course page it sits under.
    // `price` is the course price because that is what Stripe charges for
    // `productSlug`; the button label can show `lesson.price` instead, which is
    // display only and would not match the amount the buyer is taken to.
    const at = { location: 'lesson_paywall', courseSlug: course.slug, price: course.price }
    if (!user) {
      trackCheckout(EVENT.checkoutBlocked, { ...at, reason: 'signed_out' })
      // The intent travels with them. Without the checkout param they land back
      // on this page after signing up and have to find the button again, with
      // the decision already made; with it, authentication goes straight to
      // Stripe. See lib/checkoutIntent.ts for what that cost on 31 Aug.
      navigate(
        authUrlForCheckout('/signup', {
          redirect: `/courses/${slug}/${lessonSlug}`,
          productSlug: course.productSlug,
        }),
      )
      return
    }
    setCheckoutError('')
    setCheckoutBusy(true)
    try {
      const { url } = await authApi.checkout(course.productSlug)
      trackCheckout(EVENT.checkoutStart, at)
      window.location.href = url
    } catch (e) {
      const message = (e as Error).message || 'Could not start checkout. Please try again.'
      trackCheckout(EVENT.checkoutFailed, { ...at, reason: message })
      setCheckoutError(message)
      setCheckoutBusy(false)
    }
  }

  if (!lesson || !course) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-brand-cream">
        <h1 className="font-heading text-display font-bold text-brand-navy mb-4">404</h1>
        <h2 className="font-heading text-h3 font-bold text-brand-navy mb-8">Lesson Not Found</h2>
        <Link to={`/courses/${slug}`} className="font-heading text-cta font-bold text-brand-navy underline">Back to Course</Link>
      </div>
    )
  }

  return (
    <>

      <div className="min-h-screen bg-brand-cream">
        {/* Top bar */}
        <div className="bg-brand-navy text-white py-3 border-b border-white/10">
          <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <Link
              to={`/courses/${slug}`}
              className="inline-flex items-center gap-2 font-body text-body-sm text-white/80 hover:text-white transition-colors duration-150"
            >
              <FaArrowLeft className="text-xs" aria-hidden="true" />
              {course.title}
            </Link>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden inline-flex items-center gap-2 font-body text-body-sm text-white/80 hover:text-white transition-colors duration-150"
              aria-expanded={sidebarOpen}
              aria-controls="course-sidebar"
            >
              <FaBookOpen className="text-sm" aria-hidden="true" />
              Contents
            </button>
          </div>
        </div>

        <div className="max-w-9xl mx-auto flex">
          {/* Sidebar - Course navigation */}
          <aside id="course-sidebar" className={`${sidebarOpen ? 'block' : 'hidden'} lg:block w-full lg:w-80 xl:w-96 flex-shrink-0 border-r border-border-subtle bg-white lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto`}>
            <div className="p-4 border-b border-border-subtle">
              <Link to={`/courses/${slug}`} className="font-heading text-sm font-bold uppercase tracking-wide text-brand-navy hover:opacity-80 transition-opacity">
                {course.title}
              </Link>
            </div>

            <nav className="py-2">
              {modules.map((mod, modIdx) => {
                const isOpen = openModules.has(mod.slug)
                return (
                  <div key={mod.slug}>
                    <button
                      onClick={() => toggleModule(mod.slug)}
                      className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-brand-cream/50 transition-colors duration-150"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-navy/10 text-brand-navy font-heading text-xs font-bold">
                          {modIdx + 1}
                        </span>
                        <span className="font-heading text-xs font-bold uppercase tracking-wide text-gray-900 truncate">{mod.title}</span>
                      </div>
                      <span className="text-gray-400 text-xs ml-2 flex-shrink-0">
                        {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="pb-2">
                        {mod.lessons.map((l, lIdx) => {
                          const isActive = l.slug === lessonSlug
                          const locked = lockedForUser && !l.isFree
                          const kindMeta = lessonKindMeta(l.kind)
                          return (
                            <Link
                              key={l.slug}
                              to={`/courses/${slug}/${l.slug}`}
                              onClick={() => setSidebarOpen(false)}
                              className={`flex items-center gap-3 px-4 pl-8 py-2.5 font-body text-body-sm transition-colors ${
                                isActive
                                  ? 'bg-brand-navy/5 text-brand-navy border-r-2 border-brand-navy font-semibold'
                                  : kindMeta
                                    ? `${kindMeta.rowBg} text-ink-soft hover:text-brand-navy`
                                    : 'text-ink-soft hover:bg-brand-cream/50 hover:text-brand-navy'
                              }`}
                            >
                              <span
                                className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                                  kindMeta
                                    ? `${kindMeta.chipBg} ${kindMeta.chipText}`
                                    : isActive ? 'bg-brand-navy text-white' : 'bg-brand-cream text-ink-muted'
                                }`}
                                title={kindMeta ? kindMeta.label : undefined}
                              >
                                {kindMeta ? <kindMeta.Icon className="text-[9px]" aria-label={kindMeta.label} /> : lIdx + 1}
                              </span>
                              <span className="truncate flex-1">{kindMeta ? lessonDisplayTitle(l.title, kindMeta) : l.title}</span>
                              {locked ? <FaLock className="text-[10px] text-ink-muted flex-shrink-0" aria-label="Locked" /> : null}
                            </Link>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </nav>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0">
            {/* Breadcrumb + Progress */}
            <div className="border-b border-border-subtle bg-brand-cream px-4 sm:px-6 lg:px-8 py-4">
              <nav aria-label="Breadcrumb">
                <ol className="flex flex-wrap items-center gap-2 font-heading text-overline uppercase tracking-wider text-ink-muted">
                  <li><Link to="/courses" className="hover:text-brand-navy transition-colors duration-150">Courses</Link></li>
                  <li aria-hidden="true">/</li>
                  <li><Link to={`/courses/${slug}`} className="hover:text-brand-navy transition-colors duration-150">{course.title}</Link></li>
                  <li aria-hidden="true">/</li>
                  <li className="text-brand-navy" aria-current="page">{currentModule?.title}</li>
                </ol>
              </nav>
              {totalLessons > 0 && (
                <p className="font-body text-caption text-ink-muted mt-2">Lesson {lessonIndex} of {totalLessons}</p>
              )}
            </div>

            {/* Lesson content */}
            <article className="px-4 sm:px-6 lg:px-8 py-10">
              <header className="mb-10">
                <span className="inline-flex items-center gap-2 font-heading text-overline uppercase tracking-wider text-ink-muted mb-3">
                  <FaBookOpen className="text-sm" aria-hidden="true" />
                  {currentModule?.title}
                </span>
                <h1 className="font-heading text-h2 font-bold text-brand-navy">{lesson.title}</h1>
              </header>

              {/* #lesson-body is the gated region named by the paywall structured
                  data in routes/lesson.tsx. Keep the id on whichever branch renders
                  here, or the cssSelector points at nothing. */}
              {lesson.locked ? (
                <div id="lesson-body" className="rounded-smooth-lg border-2 border-border-subtle bg-white p-8 md:p-12 text-center">
                  <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-navy/5 text-brand-navy mb-5">
                    <FaLock className="text-xl" aria-hidden="true" />
                  </span>
                  <h2 className="font-heading text-h3 font-bold text-brand-navy mb-3">This lesson is part of the full course</h2>
                  <p className="font-body text-body text-ink-soft mb-8 max-w-md mx-auto">
                    The first module is free to preview. Unlock <strong>{course.title}</strong> for a one-time payment to get every lesson, forever.
                  </p>
                  <button
                    onClick={handleCheckout}
                    disabled={checkoutBusy}
                    className="inline-flex items-center justify-center gap-2 bg-brand-navy text-white font-heading text-cta font-bold px-8 py-4 rounded-smooth hover:opacity-90 transition-opacity disabled:opacity-60"
                  >
                    {checkoutBusy ? 'Starting checkout…' : `Unlock full course — $${lesson.price ?? course.price}`}
                  </button>
                  {checkoutError && <p className="font-body text-body-sm text-red-600 mt-4">{checkoutError}</p>}
                  {!user && (
                    <p className="font-body text-body-sm text-ink-muted mt-4">
                      Already bought it? <Link to={`/login?redirect=/courses/${slug}/${lessonSlug}`} className="text-brand-navy font-semibold underline">Sign in</Link>.
                    </p>
                  )}
                </div>
              ) : (
                <div id="lesson-body" className="Markdown-wrapper">
                  <LessonVideoSlot
                    video={lesson.video}
                    track={{
                      id: `lesson:${slug}/${lessonSlug}`,
                      title: lesson.title,
                      surface: 'lesson',
                      props: {
                        courseSlug: slug ?? '',
                        courseTitle: course?.title ?? '',
                        lessonSlug: lessonSlug ?? '',
                        moduleSlug: lesson.module.slug,
                        paid: course?.paid ? 'true' : 'false',
                      },
                    }}
                  />
                  <ErrorBoundary FallbackComponent={SectionErrorFallback}>
                    <ContentRenderer description_md={lesson.content ?? ''} />
                  </ErrorBoundary>
                  {/* At the END of the lesson, where somebody who has just
                      finished reading it actually is. Signed-in readers only:
                      there is nowhere to record it otherwise, and a control that
                      silently does nothing is worse than no control. */}
                  {user && slug && lessonSlug ? (
                    <LessonDoneButton
                      courseSlug={slug}
                      lessonSlug={lessonSlug}
                      initialDone={done.includes(lessonSlug)}
                      key={lessonSlug}
                    />
                  ) : null}
                </div>
              )}
            </article>

            {/* Prev / Next navigation */}
            <div className="border-t border-border-subtle px-4 sm:px-6 lg:px-8 py-8 bg-white">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                {prev ? (
                  <Link
                    to={`/courses/${slug}/${prev.slug}`}
                    className="group flex items-center gap-3 px-5 py-4 rounded-smooth-lg border border-border-subtle bg-brand-cream hover:opacity-90 transition-opacity duration-150 flex-1"
                  >
                    <FaArrowLeft className="text-ink-muted group-hover:text-brand-navy transition-colors duration-150 flex-shrink-0" aria-hidden="true" />
                    <div className="min-w-0">
                      <span className="block font-heading text-overline uppercase tracking-wider text-ink-muted mb-1">Previous</span>
                      <span className="block font-body text-body-sm text-brand-navy truncate">{prev.title}</span>
                    </div>
                  </Link>
                ) : <div className="flex-1" />}

                {next ? (
                  <Link
                    to={`/courses/${slug}/${next.slug}`}
                    className="group flex items-center justify-end gap-3 px-5 py-4 rounded-smooth-lg border border-border-subtle bg-brand-cream hover:opacity-90 transition-opacity duration-150 flex-1 text-right"
                  >
                    <div className="min-w-0">
                      <span className="block font-heading text-overline uppercase tracking-wider text-ink-muted mb-1">Next</span>
                      <span className="block font-body text-body-sm text-brand-navy truncate">{next.title}</span>
                    </div>
                    <FaArrowRight className="text-ink-muted group-hover:text-brand-navy transition-colors duration-150 flex-shrink-0" aria-hidden="true" />
                  </Link>
                ) : (
                  <Link
                    to={`/courses/${slug}`}
                    className="group flex items-center justify-end gap-3 px-5 py-4 rounded-smooth-lg border border-border-subtle bg-brand-cream hover:opacity-90 transition-opacity duration-150 flex-1 text-right"
                  >
                    <div className="min-w-0">
                      <span className="block font-heading text-overline uppercase tracking-wider text-ink-muted mb-1">Complete</span>
                      <span className="block font-body text-body-sm text-brand-navy truncate">Back to Course</span>
                    </div>
                    <FaCheckCircle className="text-brand-sky flex-shrink-0" aria-hidden="true" />
                  </Link>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  )
}

// True when the viewport is narrower than `px`. SSR-safe (starts false, so the
// server and first client paint agree) and updates on resize via matchMedia.
function useBelowWidth(px: number): boolean {
  const [below, setBelow] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia(`(max-width: ${px - 1}px)`)
    const onChange = () => setBelow(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [px])
  return below
}

export default LessonPage
